import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { alpha } from '@mui/material/styles';
import AddressBook from '../components/AddressBook';
import CustomsCard from '../components/label/CustomsCard';
import LabelActionBar from '../components/label/LabelActionBar';
import LabelPageHeader from '../components/label/LabelPageHeader';
import LabelSettingsCard from '../components/label/LabelSettingsCard';
import PackagesCard from '../components/label/PackagesCard';
import ShipToCard from '../components/label/ShipToCard';
import { useLabelForm } from '../components/label/hooks/useLabelForm';
import { useLabelJobSubmit } from '../components/label/hooks/useLabelJobSubmit';
import { buildLabelRequest, fromAddressBook, getMissingFields, isCustomsRequired, isInternational } from '../components/label/labelForm';
import { apiService } from '../services/apiService';
import AuthService from '../services/authService';
import { Address } from '../types/addressBook';

const productionBanner = (
  <Box
    role="note"
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2,
      py: 1.5,
      borderRadius: 3,
      fontSize: 13,
      bgcolor: alpha(theme.palette.warning.main, 0.14),
      color: theme.palette.mode === 'light' ? theme.palette.warning.dark : theme.palette.warning.light,
    })}
  >
    <WarningAmber sx={{ fontSize: 20 }} />
    <span>
      <strong>Production mode.</strong> Labels are billed by the carrier and can be shipped.
    </span>
  </Box>
);

const LabelPage: React.FC = () => {
  const authService = AuthService.getInstance();
  const { form, actions } = useLabelForm();
  const { status, submit, dismissError } = useLabelJobSubmit();
  const [partnerId, setPartnerId] = useState(() => authService.getSelectedPartner()?.partnerId ?? '');
  const [addressBookOpen, setAddressBookOpen] = useState(false);

  const partnerOptions = useMemo(
    () =>
      authService
        .getPartners()
        .slice()
        .sort((a, b) => a.partnerName.localeCompare(b.partnerName))
        .map((p) => ({ value: p.partnerId, label: p.partnerName })),
    [authService],
  );

  // Prefill the most recently used destination (API returns newest first).
  useEffect(() => {
    let cancelled = false;
    apiService
      .getAllAddresses()
      .then((addresses) => {
        if (!cancelled && addresses.length > 0) actions.applyAddress(fromAddressBook(addresses[0]));
      })
      .catch((error) => console.error('Failed to load last used address:', error));
    return () => {
      cancelled = true;
    };
  }, [actions]);

  const changePartner = useCallback(
    (id: string) => {
      setPartnerId(id);
      const partner = authService.getPartners().find((p) => p.partnerId === id);
      if (partner) authService.setSelectedPartner(partner);
    },
    [authService],
  );

  const openAddressBook = useCallback(() => setAddressBookOpen(true), []);
  const closeAddressBook = useCallback(() => setAddressBookOpen(false), []);
  const selectFromAddressBook = useCallback((address: Address) => actions.applyAddress(fromAddressBook(address)), [actions]);

  const customsRequired = isCustomsRequired(form.address);
  const customsIncluded = customsRequired || form.customsOptIn;
  const missing = getMissingFields(form);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (missing.length > 0 || status.phase === 'submitting') return;
    const auth = authService.getAuthState();
    const request = buildLabelRequest(form, {
      username: auth.username || '',
      password: auth.password || '',
      partnerId: auth.selectedPartner?.partnerId || '',
      partnerName: auth.selectedPartner?.partnerName || '',
    });
    submit(request, form.address);
  };

  return (
    <>
    <Box
      component="form"
      onSubmit={handleSubmit}
      aria-label="Print labels"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1040, width: '100%' }}
    >
      <LabelPageHeader testMode={form.testMode} onTestModeChange={actions.setTestMode} />
      {!form.testMode && productionBanner}

      <LabelSettingsCard
        partners={partnerOptions}
        partnerId={partnerId}
        onPartnerChange={changePartner}
        labelFormats={form.labelFormats}
        onToggleFormat={actions.toggleFormat}
        generateLabels={form.generateLabels}
        onGenerateLabelsChange={actions.setGenerateLabels}
      />

      <PackagesCard
        packages={form.packages}
        onChange={actions.updatePackage}
        onAdd={actions.addPackage}
        onDuplicate={actions.duplicatePackage}
        onRemove={actions.removePackage}
        onAddItem={actions.addItem}
        onItemChange={actions.updateItem}
        onToggleLimitedQuantity={actions.toggleLimitedQuantity}
        onToggleHazmat={actions.toggleHazmat}
        onHazmatChange={actions.updateHazmat}
        onRemoveItem={actions.removeItem}
      />

      <ShipToCard
        address={form.address}
        onFieldChange={actions.setAddressField}
        onAddressTypeChange={actions.setAddressType}
        customsIncluded={customsIncluded}
        customsRequired={customsRequired}
        international={isInternational(form.address.countryCode)}
        onToggleCustoms={actions.toggleCustomsOptIn}
        onOpenAddressBook={openAddressBook}
      />

      {customsIncluded && (
        <CustomsCard
          customs={form.customs}
          items={form.customsItems}
          onChange={actions.updateCustoms}
          onItemChange={actions.updateCustomsItem}
          onAddItem={actions.addCustomsItem}
          onRemoveItem={actions.removeCustomsItem}
        />
      )}

      {status.phase === 'error' && (
        <Alert severity="error" onClose={dismissError} sx={{ borderRadius: 3 }}>
          {status.message}
        </Alert>
      )}

      <LabelActionBar
        status={status}
        missing={missing}
        labelFormats={form.labelFormats}
        packageCount={form.packages.length}
        testMode={form.testMode}
        generateLabels={form.generateLabels}
      />
    </Box>

    {/* Outside the form: React submit events bubble through portals. */}
    <AddressBook open={addressBookOpen} onClose={closeAddressBook} onSelectAddress={selectFromAddressBook} />
    </>
  );
};

export default LabelPage;
